import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Users, Shield, Activity, Plus, Edit, XCircle, CheckCircle, RefreshCw, CreditCard, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import TenantSubscription from './TenantSubscription';
import { getPasswordChecks, isPasswordStrong } from '../lib/passwordPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, getAuthHeaders } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Company dialog state
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  
  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userDepartment, setUserDepartment] = useState('');
  const [userRoles, setUserRoles] = useState(['Reader']);
  const [userCompanyCode, setUserCompanyCode] = useState('');

  const isSuperAdmin = user?.roles?.includes('SuperAdmin');
  const isCompanyAdmin = user?.roles?.includes('CompanyAdmin') || isSuperAdmin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    
    try {
      // Load companies (SuperAdmin only)
      if (isSuperAdmin) {
        const companiesRes = await axios.get(`${API_URL}/api/companies/`, { headers });
        setCompanies(companiesRes.data);
      }
      
      // Load users (CompanyAdmin and SuperAdmin)
      if (isCompanyAdmin) {
        const usersRes = await axios.get(`${API_URL}/api/users/`, { headers });
        setUsers(usersRes.data);
      }
      
      // Load audit logs
      if (isSuperAdmin || user?.roles?.includes('CompanyAdmin')) {
        const auditRes = await axios.get(`${API_URL}/api/audit/logs?limit=50`, { headers });
        setAuditLogs(auditRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/companies/`, {
        name: companyName,
        code: companyCode,
        description: companyDescription,
        contact_email: companyEmail,
        contact_phone: companyPhone,
        address: companyAddress
      }, { headers });
      
      toast.success('Company created successfully');
      setCompanyDialogOpen(false);
      resetCompanyForm();
      loadData();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleToggleCompanyStatus = async (companyId, currentStatus) => {
    try {
      const headers = getAuthHeaders();
      const action = currentStatus === 'Active' ? 'deactivate' : 'activate';
      await axios.post(`${API_URL}/api/companies/${companyId}/${action}`, {}, { headers });
      
      toast.success(`Company ${action}d successfully`);
      loadData();
    } catch (error) {
      console.error('Error toggling company status:', error);
      toast.error('Failed to update company status');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // SECURITY (CWE-521): block weak passwords client-side; server re-validates.
    if (!isPasswordStrong(userPassword, userEmail)) {
      toast.error('Please choose a password that meets all the requirements below.');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/users/`, {
        email: userEmail,
        password: userPassword,
        company_code: userCompanyCode || user?.company_code,
        full_name: userName,
        phone: userPhone,
        department: userDepartment,
        roles: userRoles
      }, { headers });
      
      toast.success('User created successfully');
      setUserDialogOpen(false);
      resetUserForm();
      loadData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const headers = getAuthHeaders();
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      await axios.post(`${API_URL}/api/users/${userId}/${action}`, {}, { headers });
      
      toast.success(`User ${action}d successfully`);
      loadData();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const resetCompanyForm = () => {
    setCompanyName('');
    setCompanyCode('');
    setCompanyDescription('');
    setCompanyEmail('');
    setCompanyPhone('');
    setCompanyAddress('');
  };

  const resetUserForm = () => {
    setUserName('');
    setUserEmail('');
    setUserPassword('');
    setUserPhone('');
    setUserDepartment('');
    setUserRoles(['Reader']);
    setUserCompanyCode('');
  };

  if (!isCompanyAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Access Denied</h3>
            <p className="text-slate-500">Only administrators can access settings</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Manage companies, users, and system settings</p>
      </div>

      <Tabs defaultValue={isSuperAdmin ? "companies" : "users"} className="w-full">
        <TabsList>
          {isSuperAdmin && (
            <TabsTrigger value="companies" data-testid="companies-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Companies
            </TabsTrigger>
          )}
          {isCompanyAdmin && (
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          )}
          <TabsTrigger value="audit" data-testid="audit-tab">
            <Activity className="w-4 h-4 mr-2" />
            Audit Logs
          </TabsTrigger>
          {isCompanyAdmin && (
            <TabsTrigger value="system" data-testid="system-tab">
              <Shield className="w-4 h-4 mr-2" />
              System
            </TabsTrigger>
          )}
          {isCompanyAdmin && (
            <TabsTrigger value="subscription" data-testid="subscription-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </TabsTrigger>
          )}
        </TabsList>

        {/* Companies Management (SuperAdmin Only) */}
        {isSuperAdmin && (
          <TabsContent value="companies" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Company Management</CardTitle>
                    <CardDescription>Create and manage companies in the system</CardDescription>
                  </div>
                  <Button onClick={() => setCompanyDialogOpen(true)} data-testid="create-company-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Company
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : companies.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No companies found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {companies.map((company) => (
                      <div
                        key={company.id}
                        data-testid={`company-${company.code}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Building2 className="w-8 h-8 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-slate-900">{company.name}</h3>
                            <p className="text-sm text-slate-500">Code: {company.code}</p>
                            {company.description && (
                              <p className="text-sm text-slate-600 mt-1">{company.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs ${
                              company.status === 'Active'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {company.status}
                          </span>
                          {company.code !== 'REASON' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleCompanyStatus(company.id, company.status)}
                              data-testid={`toggle-company-${company.code}`}
                            >
                              {company.status === 'Active' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Users Management (CompanyAdmin and SuperAdmin) */}
        {isCompanyAdmin && (
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>
                      Manage users {isSuperAdmin ? 'in all companies' : 'in your company'}
                    </CardDescription>
                  </div>
                  <Button onClick={() => setUserDialogOpen(true)} data-testid="create-user-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No users found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.map((userItem) => (
                      <div
                        key={userItem.id}
                        data-testid={`user-${userItem.email}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Users className="w-8 h-8 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-slate-900">{userItem.full_name || userItem.email}</h3>
                            <p className="text-sm text-slate-500">{userItem.email} • {userItem.company_code}</p>
                            <div className="flex gap-1 mt-1">
                              {userItem.roles.map((role, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded">
                                  {role}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs ${
                              userItem.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {userItem.status}
                          </span>
                          {userItem.id !== user.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => axios.post(`${API_URL}/api/users/${userItem.id}/${userItem.status === 'active' ? 'deactivate' : 'activate'}`, {}, {headers: getAuthHeaders()}).then(loadData)}
                              data-testid={`toggle-user-${userItem.email}`}
                            >
                              {userItem.status === 'active' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Audit Logs */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
              <CardDescription>Recent system activity</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No audit logs available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      data-testid={`audit-log-${log.id}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Activity className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{log.action}</p>
                          <p className="text-xs text-slate-500">
                            {log.resource_type} - {log.resource_id}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400">{log.actor_id}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Management (Admin Only) */}
        {isCompanyAdmin && (
          <TabsContent value="system" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI Search Index</CardTitle>
                <CardDescription>Manage ChromaDB document indexing for AI search</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-2">Index All Documents</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Re-index all documents in the system to enable AI-powered search and Q&A.
                    This may take a few minutes depending on the number of documents.
                  </p>
                  <Button 
                    onClick={async () => {
                      try {
                        const headers = getAuthHeaders();
                        const response = await axios.post(`${API_URL}/api/documents/index-all`, {}, { headers });
                        const data = response.data;
                        if (data.indexed > 0) {
                          toast.success(`Indexing started for ${data.indexed} documents out of ${data.total_documents} total`);
                        } else {
                          toast.info(`${data.total_documents} documents found. ${data.skipped || 0} already indexed. ${data.indexed} new documents queued for indexing.`);
                        }
                      } catch (error) {
                        console.error('Error indexing:', error);
                        toast.error(error.response?.data?.detail || 'Failed to start indexing');
                      }
                    }}
                    data-testid="index-all-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Index All Documents
                  </Button>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-slate-900 mb-2">Reprocess All Documents</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Clear all existing AI summaries and re-process every document with the latest AI pipeline.
                    Use this after system updates to refresh stale or incorrect AI results.
                    Only documents with actual files will be reprocessed.
                  </p>
                  <Button 
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={async () => {
                      if (!window.confirm('This will clear ALL existing AI summaries and re-analyze every document. This may take several minutes. Continue?')) return;
                      try {
                        const headers = getAuthHeaders();
                        const response = await axios.post(`${API_URL}/api/documents/reprocess-all`, {}, { headers });
                        const data = response.data;
                        toast.success(`Reprocessing started: ${data.queued} documents queued, ${data.cleared} stale results cleared, ${data.skipped_no_file} skipped (no file)`);
                      } catch (error) {
                        console.error('Error reprocessing:', error);
                        toast.error(error.response?.data?.detail || 'Failed to start reprocessing');
                      }
                    }}
                    data-testid="reprocess-all-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reprocess All Documents
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        {isCompanyAdmin && (
          <TabsContent value="subscription" className="space-y-4">
            <TenantSubscription embedded />
          </TabsContent>
        )}
      </Tabs>

      {/* Create Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Company Name *</label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                required
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Company Code *</label>
              <Input
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                placeholder="e.g., ACME"
                required
                data-testid="company-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Input
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
                placeholder="Brief description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Email</label>
              <Input
                type="email"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Phone</label>
              <Input
                value={companyPhone}
                onChange={(e) => setCompanyPhone(e.target.value)}
                placeholder="+1-555-0100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Address</label>
              <Input
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
                placeholder="Company address"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1" data-testid="submit-company-btn">
                Create Company
              </Button>
              <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            {isSuperAdmin && (
              <div>
                <label className="block text-sm font-medium mb-2">Company Code *</label>
                <Input
                  value={userCompanyCode}
                  onChange={(e) => setUserCompanyCode(e.target.value.toUpperCase())}
                  placeholder="DEMO or ACME"
                  required
                  data-testid="user-company-code-input"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Full Name *</label>
              <Input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="John Doe"
                required
                data-testid="user-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <Input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="john@example.com"
                required
                data-testid="user-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password *</label>
              <Input
                type="password"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                placeholder="Strong password"
                required
                data-testid="user-password-input"
              />
              {userPassword.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="user-password-requirements">
                  {getPasswordChecks(userPassword, userEmail).map((c) => (
                    <li
                      key={c.key}
                      data-testid={`user-pw-check-${c.key}`}
                      data-ok={c.ok}
                      className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-emerald-600' : 'text-slate-400'}`}
                    >
                      {c.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {c.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone</label>
              <Input
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                placeholder="+1-555-0100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Department</label>
              <Input
                value={userDepartment}
                onChange={(e) => setUserDepartment(e.target.value)}
                placeholder="e.g., Operations"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Roles *</label>
              <Select value={userRoles[0]} onValueChange={(val) => setUserRoles([val])}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Reader">Reader</SelectItem>
                  <SelectItem value="Contributor">Contributor</SelectItem>
                  <SelectItem value="Approver">Approver</SelectItem>
                  <SelectItem value="RecordsManager">Records Manager</SelectItem>
                  {isSuperAdmin && <SelectItem value="CompanyAdmin">Company Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1" data-testid="submit-user-btn">
                Create User
              </Button>
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
